/**
 * The app's half of the sandbox conversation.
 *
 * Kept out of the component because the interesting part is a state machine —
 * loading, running, failed — driven by messages from a window the app cannot
 * inspect. All it can do is talk to it and believe what comes back, which is
 * why the frame is never given anything worth lying about.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ANIM_PROTOCOL_VERSION,
  isAnimEvent,
  type AnimCommand,
  type AnimTheme,
} from "../../shared/anim-protocol.ts";

export const SANDBOX_URL = "/sandbox/runner.html";

export type AnimStatus = "loading" | "running" | "failed";

interface UseAnimFrameOptions {
  readonly code: string;
  readonly theme: AnimTheme;
  readonly reducedMotion: boolean;
}

interface AnimFrameApi {
  readonly frameRef: React.RefObject<HTMLIFrameElement | null>;
  readonly status: AnimStatus;
  readonly error: string | null;
  readonly playing: boolean;
  readonly setPlaying: (playing: boolean) => void;
  /** Pause without touching what the reader chose, e.g. when scrolled away. */
  readonly suspend: (suspended: boolean) => void;
}

export function useAnimFrame({
  code,
  theme,
  reducedMotion,
}: UseAnimFrameOptions): AnimFrameApi {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [status, setStatus] = useState<AnimStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlayingState] = useState(!reducedMotion);

  /** What the reader last asked for, so leaving and returning restores it. */
  const wanted = useRef(!reducedMotion);

  /**
   * The frame's origin is opaque, so `"*"` is the only target that can reach
   * it. Safe here and nowhere else: every command carries the sketch the
   * reader is already looking at, plus theme colours. No session, no
   * transcript, no identity.
   */
  const post = useCallback((command: AnimCommand) => {
    frameRef.current?.contentWindow?.postMessage(command, "*");
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Authentication by window identity. `event.origin` is the string
      // "null" for a sandboxed frame and proves nothing on its own.
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      if (!isAnimEvent(event.data)) return;

      switch (event.data.type) {
        case "ready":
          post({
            v: ANIM_PROTOCOL_VERSION,
            type: "run",
            code,
            theme,
            reducedMotion,
          });
          setStatus("running");
          break;
        case "state":
          setPlayingState(event.data.playing);
          break;
        case "error":
          setStatus("failed");
          setError(event.data.message);
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // `code` is fixed for the lifetime of a frame — the block remounts on a
    // new sketch — so re-running this on theme alone is what we want.
  }, [code, theme, reducedMotion, post]);

  // A figure drawn for daylight and read at night is worse than no figure.
  useEffect(() => {
    if (status === "running") {
      post({ v: ANIM_PROTOCOL_VERSION, type: "theme", theme });
    }
  }, [theme, status, post]);

  const setPlaying = useCallback(
    (next: boolean) => {
      wanted.current = next;
      setPlayingState(next);
      post({ v: ANIM_PROTOCOL_VERSION, type: "setPlaying", playing: next });
    },
    [post],
  );

  const suspend = useCallback(
    (suspended: boolean) => {
      const next = suspended ? false : wanted.current;
      setPlayingState(next);
      post({ v: ANIM_PROTOCOL_VERSION, type: "setPlaying", playing: next });
    },
    [post],
  );

  return { frameRef, status, error, playing, setPlaying, suspend };
}
